import React, { useCallback, useEffect, useRef } from 'react';
import { AppState, Platform, View, ActivityIndicator, StyleSheet } from 'react-native';
import { Slot, useRouter, useSegments } from 'expo-router';
import * as Notifications from 'expo-notifications';
import NetInfo from '@react-native-community/netinfo';
import type { User } from '@supabase/supabase-js';
import {
  useFonts,
  Fredoka_400Regular,
  Fredoka_500Medium,
  Fredoka_600SemiBold,
  Fredoka_700Bold,
} from '@expo-google-fonts/fredoka';
import {
  Nunito_400Regular,
  Nunito_600SemiBold,
  Nunito_700Bold,
  Nunito_800ExtraBold,
} from '@expo-google-fonts/nunito';
import '../src/i18n';
import '../src/features/notifications/notificationHandler';
import { useAuth } from '../src/features/auth/useAuth';
import { DbProvider, useDb } from '../src/db/client';
import { isOnboardingPending } from '../src/features/onboarding/onboardingFlag';
import { restorePendingBrightnessIfDirty } from '../src/features/session/screenEnergy';
import { DAILY_REMINDER_KIND } from '../src/features/notifications/dailyReminder';
import { listProjects } from '../src/features/projects/projectRepository';
import { filterAndSortProjects } from '../src/domain/projectList';
import { SyncServiceImpl } from '../src/sync';
import { purgeSoftDeleted, purgeDeletedPhotos, purgeProcessedOutboxItems } from '../src/sync/purge';
import { isOnline } from '../src/sync/netStatus';

/**
 * Root layout: single source of truth for auth state (one `useAuth()` call,
 * one Supabase listener) and the auth-gate itself.
 *
 * Pattern: redirect based on auth state (Expo Router's recommended approach)
 * instead of conditionally hiding children — the (auth), (tabs) and
 * `onboarding` routes always mount, we just steer `router` to the right one
 * once we know whether there's a user (and, if there is, whether the
 * mini-onboarding pós-cadastro — spec S01, Etapa 9 — ainda está pendente
 * para ela).
 *
 * `DbProvider` (src/db/client.ts) abre a conexão SQLite local e roda a
 * migração no primeiro boot, disponibilizando o `db` pra árvore inteira via
 * `useDb()` — usado pelo indicador de sync em Perfil, pela tela de
 * onboarding, pelo `NotificationDeepLinkListener` (Etapa 14) e pelo
 * `AutoSyncListener` (Etapa 15) abaixo.
 */
export default function RootLayout() {
  const { user, loading } = useAuth();
  const segments = useSegments();
  const router = useRouter();
  const [fontsLoaded] = useFonts({
    Fredoka_400Regular,
    Fredoka_500Medium,
    Fredoka_600SemiBold,
    Fredoka_700Bold,
    Nunito_400Regular,
    Nunito_600SemiBold,
    Nunito_700Bold,
    Nunito_800ExtraBold,
  });

  // Brilho "sujo" (Etapa 12, spec S03 §Tela e energia): se a tela de Sessão
  // reduziu o brilho e o app foi morto antes de conseguir restaurar, este é
  // o próximo lugar que roda — independe de estado de auth (é uma
  // configuração do dispositivo, não da conta). Ver
  // src/features/session/screenEnergy.ts / brightnessFlag.ts.
  useEffect(() => {
    if (Platform.OS !== 'web') {
      restorePendingBrightnessIfDirty().catch((error) => {
        console.error('RootLayout: falha ao restaurar brilho pendente no boot', error);
      });
    }
  }, []);

  useEffect(() => {
    if (loading) return;

    let cancelled = false;

    async function evaluateRoute() {
      const inAuthGroup = segments[0] === '(auth)';

      if (!user) {
        if (!inAuthGroup) router.replace('/(auth)/signin');
        return;
      }

      // Recuperação de senha (spec S01): o deep link do e-mail estabelece
      // uma sessão de verdade via `setSession` (ver
      // `useAuth.completePasswordRecovery` / `ResetPasswordScreen`), então
      // `user` já vem preenchido aqui mesmo a usuária ainda não tendo
      // definido a nova senha. Sem este corte, o gate abaixo trataria isso
      // como um login normal e chutaria a usuária para /onboarding ou
      // /(tabs) antes de ela conseguir ver o formulário. `ResetPasswordScreen`
      // navega ela mesma para /(tabs) ao terminar.
      if (inAuthGroup && segments[1] === 'reset-password') return;

      // Relida a cada navegação (não guardada em estado do componente): o
      // flag muda por fora deste effect (a tela de onboarding limpa o
      // próprio flag e navega ela mesma para /(tabs) ao terminar) — reler
      // aqui a cada mudança de `segments` garante que este gate sempre vê o
      // valor atual, em vez de agir sobre um valor obtido antes daquela
      // navegação (o que causaria um loop redirecionando de volta para
      // /onboarding logo depois dela terminar).
      const pending = await isOnboardingPending(user.id);
      if (cancelled) return;

      const inOnboarding = segments[0] === 'onboarding';

      if (pending && !inOnboarding) {
        router.replace('/onboarding');
      } else if (!pending && (inAuthGroup || inOnboarding)) {
        router.replace('/(tabs)');
      }
    }

    evaluateRoute();

    return () => {
      cancelled = true;
    };
  }, [user, loading, segments, router]);

  if (loading || !fontsLoaded) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  return (
    <DbProvider>
      <NotificationDeepLinkListener user={user} authLoading={loading} />
      <AutoSyncListener user={user} authLoading={loading} />
      <Slot />
    </DbProvider>
  );
}

// Uma única instância do serviço de sync para o app inteiro (mesmo padrão de
// ProfileScreen.tsx: sem estado próprio além do client Supabase que ele cria/
// recebe — seguro reutilizar).
const syncService = new SyncServiceImpl();

/**
 * Sync automático em background (Etapa 15 — spec S05 §Estados na UI):
 * substitui o botão manual "Sincronizar agora". Roda em silêncio — erro
 * nunca vira Alert, só log (`console.error`); sync nunca pode bloquear a UI.
 *
 * Gatilhos: app abre (db pronto + usuário logado), volta ao foreground
 * (`AppState`, mesmo padrão de SessionScreen.tsx), rede reconecta
 * (`NetInfo`, borda offline→online). `syncAll` já faz push (drena a fila
 * inteira, ver src/sync/index.ts) seguido de pull, então isso cobre "após
 * cada push" da spec sem precisar de gatilho à parte.
 *
 * Depois de um `syncAll` bem-sucedido, roda o purge de fundo
 * (src/sync/purge.ts) — nunca no caminho bloqueante do sync, e uma falha no
 * purge nunca interrompe nem reporta erro do sync em si (só log).
 *
 * Mesmo padrão de `NotificationDeepLinkListener`: vive DENTRO de
 * `DbProvider` (precisa de `useDb()`), renderiza `null`.
 */
function AutoSyncListener({ user, authLoading }: { user: User | null; authLoading: boolean }) {
  const { db, ready } = useDb();
  const syncingRef = useRef(false);
  const wasOnlineRef = useRef(true);

  const runSync = useCallback(async () => {
    if (!db || !ready || !user || authLoading || syncingRef.current) return;
    syncingRef.current = true;
    try {
      await syncService.syncAll(db);
      try {
        await purgeSoftDeleted(db);
        await purgeDeletedPhotos(db);
        await purgeProcessedOutboxItems(db);
      } catch (error) {
        console.error('AutoSyncListener: falha no purge de fundo (não afeta o sync)', error);
      }
    } catch (error) {
      console.error('AutoSyncListener: falha ao sincronizar automaticamente', error);
    } finally {
      syncingRef.current = false;
    }
  }, [db, ready, user, authLoading]);

  // App abre / usuário loga / db fica pronto.
  useEffect(() => {
    runSync();
  }, [runSync]);

  // App volta ao foreground.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') runSync();
    });
    return () => subscription.remove();
  }, [runSync]);

  // Rede reconecta (borda offline→online, não todo evento).
  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener((state) => {
      const online = isOnline(state);
      if (online && !wasOnlineRef.current) runSync();
      wasOnlineRef.current = online;
    });
    return unsubscribe;
  }, [runSync]);

  return null;
}

/**
 * Deep link do lembrete diário de "projeto atual" (Etapa 14 — spec S03
 * §Notificação "projeto atual"): ao tocar na notificação, abre a Sessão do
 * projeto EM ANDAMENTO mais recente (não "qualquer projeto recente" — um
 * projeto finalizado/cancelado não deve reabrir a tela de cronômetro).
 *
 * Precisa estar DENTRO de `<DbProvider>` (não em `RootLayout`) porque
 * `useDb()` só resolve dentro do provider que o próprio `RootLayout`
 * declara. Renderiza `null` — é só um listener.
 *
 * Cobre dois casos de entrega da mesma resposta de notificação:
 *  - App já aberto, usuária toca na notificação →
 *    `addNotificationResponseReceivedListener`.
 *  - App foi ABERTO pelo toque (cold start) → o listener acima pode não
 *    pegar a resposta a tempo; `getLastNotificationResponseAsync` cobre
 *    esse caso. As duas fontes podem entregar a MESMA resposta — dedupe por
 *    `request.identifier` evita navegar duas vezes.
 *
 * Só processa depois que auth E db estão prontos (nunca navega para uma
 * rota autenticada com uma sessão desconhecida/deslogada).
 */
function NotificationDeepLinkListener({ user, authLoading }: { user: User | null; authLoading: boolean }) {
  const { db, ready } = useDb();
  const router = useRouter();
  const handledIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (Platform.OS === 'web' || authLoading || !ready) return;

    let cancelled = false;

    async function handle(response: Notifications.NotificationResponse) {
      const data = response.notification.request.content.data as { kind?: string } | undefined;
      if (data?.kind !== DAILY_REMINDER_KIND) return;

      const id = response.notification.request.identifier;
      if (handledIds.current.has(id)) return;
      handledIds.current.add(id);

      if (!user || !db) return;

      try {
        const projects = await listProjects(db, user.id);
        const [mostRecent] = filterAndSortProjects(projects, { status: 'in_progress' });
        if (mostRecent && !cancelled) router.push(`/sessao/${mostRecent.id}`);
      } catch (error) {
        console.error('RootLayout: falha ao abrir sessão a partir da notificação', error);
      }
    }

    Notifications.getLastNotificationResponseAsync().then((response) => {
      if (response) handle(response);
    });

    const subscription = Notifications.addNotificationResponseReceivedListener(handle);
    return () => {
      cancelled = true;
      subscription.remove();
    };
  }, [authLoading, ready, user, db, router]);

  return null;
}

const styles = StyleSheet.create({
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
});
